import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"

/** Width of a LANDSCAPE_INSERT face, locked by `.visualiser--landscape-insert` in the face CSS. */
export const UV_INSERT_FACE_WIDTH_PX = 315

/**
 * How much to shrink a fixed-width insert face so it fits the panel it is sitting in.
 *
 * Down-scale only. At a 375px viewport the 315px face already fits, so the only panels that get a
 * transform are the very narrowest phones; nothing is ever scaled up. That matters because a
 * `transform` ancestor over a `<textarea>` misplaces the caret on iOS Safari, so the face is left
 * untransformed wherever it can be.
 *
 * @param options.panelWidth - Measured width of the panel, in CSS pixels. 0/NaN means unmeasured.
 * @param options.faceWidth - Width of the face being scaled. Defaults to the landscape insert.
 * @returns A scale factor in (0, 1]. Always 1 when the panel is unmeasured or wide enough.
 */
export function computeFaceScale({
  panelWidth,
  faceWidth = UV_INSERT_FACE_WIDTH_PX,
}: {
  panelWidth: number
  faceWidth?: number
}): number {
  if (!Number.isFinite(panelWidth) || panelWidth <= 0) return 1
  if (!Number.isFinite(faceWidth) || faceWidth <= 0) return 1
  return Math.min(1, panelWidth / faceWidth)
}

/**
 * Measures the panel and reports the scale its face should render at.
 *
 * `ResizeObserver` is absent in jsdom (and in a few old browsers), so the hook measures once on
 * mount and simply stops observing when it is missing — the face then renders at 1:1, which is the
 * correct result for every panel wide enough to hold it.
 */
export function useFaceScalerViewModel({ faceWidth = UV_INSERT_FACE_WIDTH_PX }: { faceWidth?: number } = {}) {
  const [panelWidth, setPanelWidth] = useState(0)
  const panelRef = useRef<HTMLDivElement | null>(null)

  const measure = useCallback(() => {
    const node = panelRef.current
    if (!node) return
    setPanelWidth(node.getBoundingClientRect().width)
  }, [])

  useLayoutEffect(() => {
    measure()
  }, [measure])

  useEffect(() => {
    const node = panelRef.current
    if (!node || typeof ResizeObserver === "undefined") return undefined

    const observer = new ResizeObserver(() => measure())
    observer.observe(node)

    return () => observer.disconnect()
  }, [measure])

  return { panelRef, scale: computeFaceScale({ panelWidth, faceWidth }) }
}
